<script>
import { groups } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { line, curveCardinal } from 'd3-shape';
import YAxis from './components/yAxis.svelte';
import Globe from './components/Globe.svelte';

let { width, data } = $props();
const margin = { left: 0, top: 14, right: 60, bottom: 28 };

// Strong "super" El Niño seasons to highlight in red, keyed by the year the
// season starts (e.g. 1982 => the 1982-83 season).
const ninos = [1982, 1997, 2015];
const currentYear = 2026;

// Day-of-year (non-leap) for the 1st of each month. Used to place points by
// their actual date so weekly values align across years regardless of leap
// years.
const MONTH_DOY = [1, 32, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335];

// Each line represents an El Niño "season" running from the start month of one
// year into the following year, so the x-axis starts at SEASON_START_MONTH.
const SEASON_START_MONTH = 6; // June
const SEASON_END_MONTH = SEASON_START_MONTH === 1 ? 12 : SEASON_START_MONTH - 1;
const SEASON_START_DOY = MONTH_DOY[SEASON_START_MONTH - 1];
const SEASON_LENGTH = 365;
const SEASON_START = 0;
const SEASON_END = SEASON_LENGTH;

// Convert a 'YYYY-MM-DD' string to a normalized day-of-year (1–365).
const dayOfYear = dt => {
    const [, m, day] = dt.split('-').map(Number);
    return MONTH_DOY[m - 1] + (day - 1);
};

// Position a date along the season axis: the start month's 1st -> 0, through
// the last day before the next season -> 364.
const seasonDay = dt => {
    const raw = dayOfYear(dt) - SEASON_START_DOY;
    return raw < 0 ? raw + SEASON_LENGTH : raw;
};

// The season a date belongs to, labeled by its starting year. Months from the
// start month through December belong to that calendar year; earlier months
// roll into the previous year.
const seasonYear = dt => {
    const [y, m] = dt.split('-').map(Number);
    return m >= SEASON_START_MONTH ? y : y - 1;
};

// Season-day for the 1st of a given month (used to place x-axis ticks).
const monthSeasonDay = month =>
    (MONTH_DOY[month - 1] - SEASON_START_DOY + SEASON_LENGTH) % SEASON_LENGTH;

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Build the month ticks starting at the season start month and wrapping around.
const months = Array.from({ length: 12 }, (_, i) => {
    const month = ((SEASON_START_MONTH - 1 + i) % 12) + 1;
    return { label: MONTH_LABELS[month - 1], month, sday: monthSeasonDay(month) };
});

let height = $state();

let chartwidth = $derived(width - margin.left - margin.right);
let chartheight = $derived(height - margin.top - margin.bottom);

let filtered = $derived(data ?? null);

// Group by season (April–March) and sort so gray background lines draw first,
// highlighted El Niño seasons next, and the current season last (on top).
let grouped = $derived.by(() => {
    if (!filtered) return null;
    const rank = d => (d.isCurrent ? 2 : d.isNino ? 1 : 0);
    return groups(filtered, d => seasonYear(d.dt))
        .map(([year, values]) => ({
            year,
            values: values.slice().sort((a, b) => a.dt.localeCompare(b.dt)),
            isNino: ninos.includes(year),
            isCurrent: year === currentYear,
        }))
        .sort((a, b) => rank(a) - rank(b));
});

// Split lines so the axis can sit in front of the gray background years but
// behind the highlighted (red) years.
let backgroundSeries = $derived(grouped ? grouped.filter(s => !s.isNino && !s.isCurrent) : []);
let foregroundSeries = $derived(grouped ? grouped.filter(s => s.isNino || s.isCurrent) : []);

let xScale = $derived(
    chartwidth ? scaleLinear().domain([SEASON_START, SEASON_END]).range([0, chartwidth]) : null
);
let yScale = $derived(
    chartheight ? scaleLinear().domain([3, -2.5]).range([0, chartheight]) : null
);

let lineGen = $derived(
    xScale && yScale
        ? line()
              .x(d => xScale(seasonDay(d.dt)))
              .y(d => yScale(+d.roni))
              .curve(curveCardinal)
        : null
);

// End point of the current-year line, for the pulsing marker + label
let currentSeries = $derived(grouped ? grouped.find(d => d.isCurrent) : null);
let lastPoint = $derived(
    currentSeries && currentSeries.values.length
        ? currentSeries.values[currentSeries.values.length - 1]
        : null
);

const fmt = v => (v > 0 ? '+' : '') + v.toFixed(1);

// Label a year as an ENSO season, e.g. 1982 -> "1982-83", 1999 -> "1999-00"
const fmtSeason = y => `${y}-${String((y + 1) % 100).padStart(2, '0')}`;

// Parse as a local date to avoid timezone shifting the day
const parseDate = d => {
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day);
};

const fmtDate = d =>
    d
        ? parseDate(d).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : '';

const fmtShortDate = d =>
    d
        ? parseDate(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '';

// Hover state
let svgEl = $state();
let hovered = $state(null); // hovered series
let hoverPoint = $state(null); // nearest data point under the cursor
let pointer = $state({ x: 0, y: 0 }); // cursor position for the tooltip

// Flip the tooltip to the left of the cursor near the right edge so it
// doesn't get clipped.
let tooltipFlip = $derived(width ? pointer.x > width * 0.6 : false);

// End-of-line labels for the highlighted El Niño years (the current year
// already has its own marker + label).
// Highlighted years whose end-of-line label is suppressed to avoid crowding
const hiddenLabels = [];

// Minimum vertical spacing between end-of-line labels
const LABEL_GAP = 14;

let ninoLabels = $derived.by(() => {
    if (!grouped || !xScale || !yScale) return [];
    const labels = grouped
        .filter(s => s.isNino && s.values.length && !hiddenLabels.includes(s.year))
        .map(s => {
            const last = s.values[s.values.length - 1];
            const anchorY = yScale(+last.roni);
            return { year: s.year, x: xScale(seasonDay(last.dt)), anchorY, y: anchorY };
        })
        .sort((a, b) => a.anchorY - b.anchorY);
    // Nudge labels apart so they don't overlap, keeping the topmost anchored.
    for (let i = 1; i < labels.length; i++) {
        if (labels[i].y - labels[i - 1].y < LABEL_GAP) {
            labels[i].y = labels[i - 1].y + LABEL_GAP;
        }
    }
    return labels;
});

function handleHover(e, s) {
    if (!svgEl || !xScale) return;
    const rect = svgEl.getBoundingClientRect();
    const sday = xScale.invert(e.clientX - rect.left - margin.left);
    let nearest = s.values[0];
    let best = Infinity;
    for (const p of s.values) {
        const dist = Math.abs(seasonDay(p.dt) - sday);
        if (dist < best) {
            best = dist;
            nearest = p;
        }
    }
    hovered = s;
    hoverPoint = nearest;
    pointer = { x: e.clientX - rect.left, y: e.clientY - rect.top };
}

function clearHover() {
    hovered = null;
    hoverPoint = null;
}
</script>


<div class="graphic-wrapper" bind:clientWidth={width} bind:clientHeight={height}>
    <div class="locator">
        <Globe size={84} />
    </div>

    {#if width && height}
        <svg bind:this={svgEl} {width} {height}>
            <g class="chart" transform="translate({margin.left}, {margin.top})">
                {#if yScale}
                    <rect
                        class="nino-band"
                        x="0"
                        y="0"
                        width={chartwidth}
                        height={yScale(0.5)}
                    ></rect>
                {/if}

                {#if xScale}
                    <g class="x-axis">
                        {#each months as m}
                            <text
                                class="month-label"
                                class:start={m.month === SEASON_START_MONTH}
                                class:end={m.month === SEASON_END_MONTH}
                                x={xScale(m.sday)}
                                y={chartheight + 16}
                            >{width < 450 ? m.label.charAt(0) : m.label}</text>
                        {/each}
                    </g>
                {/if}

                {#if grouped && lineGen}
                    <g class="lines" class:dim={hovered}>
                        {#each backgroundSeries as s (s.year)}
                            <path
                                d={lineGen(s.values)}
                                class="year-line"
                                fill="none"
                            ></path>
                        {/each}
                    </g>

                    {#if yScale}
                        <YAxis {yScale} {chartwidth} {width} />
                    {/if}

                    <g class="lines" class:dim={hovered}>
                        {#each foregroundSeries as s (s.year)}
                            <path
                                d={lineGen(s.values)}
                                class="year-line"
                                class:nino={s.isNino}
                                class:current={s.isCurrent}
                                fill="none"
                            ></path>
                        {/each}
                    </g>

                    {#if hovered}
                        <path
                            class="hover-line"
                            class:nino={hovered.isNino}
                            class:current={hovered.isCurrent}
                            d={lineGen(hovered.values)}
                            fill="none"
                        ></path>
                    {/if}

                    {#if yScale}
                        <text class="nino-band-label halo" x={chartwidth / 2} y={yScale(0.5) - 6}>El Niño conditions</text>
                        <text class="nino-band-label" x={chartwidth / 2} y={yScale(0.5) - 6}>El Niño conditions</text>
                    {/if}

                    {#each ninoLabels as l (l.year)}
                        {#if Math.abs(l.y - l.anchorY) > 1}
                            <line
                                class="year-leader"
                                x1={l.x}
                                y1={l.anchorY}
                                x2={l.x + 5}
                                y2={l.y}
                            ></line>
                        {/if}
                        <text class="year-label" x={l.x + 6} y={l.y + 4}>{fmtSeason(l.year)}</text>
                    {/each}

                    {#each grouped as s (s.year)}
                        <path
                            class="hit-area"
                            d={lineGen(s.values)}
                            fill="none"
                            role="presentation"
                            onpointerenter={(e) => handleHover(e, s)}
                            onpointermove={(e) => handleHover(e, s)}
                            onpointerleave={clearHover}
                        ></path>
                    {/each}
                {/if}

                {#if hoverPoint && xScale && yScale}
                    <circle
                        class="hover-dot"
                        class:nino={hovered && hovered.isNino}
                        class:current={hovered && hovered.isCurrent}
                        cx={xScale(seasonDay(hoverPoint.dt))}
                        cy={yScale(+hoverPoint.roni)}
                        r="4"
                    ></circle>
                {/if}

                {#if lastPoint && xScale && yScale}
                    {@const cx = xScale(seasonDay(lastPoint.dt))}
                    {@const cy = yScale(+lastPoint.roni)}
                    <!-- Flip the label to the right of the dot when the marker
                         sits in the left half, so it doesn't clip on mobile. -->
                    {@const flip = cx < chartwidth / 2}
                    {@const labelX = flip ? 11 : -11}
                    {@const anchor = flip ? 'start' : 'end'}
                    <g class="current-marker" transform="translate({cx}, {cy})">
                        <circle class="pulse" r="5"></circle>
                        <circle class="dot" r="4"></circle>
                        <text class="marker-year halo" x={labelX} y="-2" text-anchor={anchor}>{fmtDate(lastPoint.dt)}</text>
                        <text class="marker-year" x={labelX} y="-2" text-anchor={anchor}>{fmtDate(lastPoint.dt)}</text>
                        <text class="marker-value halo" x={labelX} y="12" text-anchor={anchor}>{fmt(+lastPoint.roni)}°C</text>
                        <text class="marker-value" x={labelX} y="12" text-anchor={anchor}>{fmt(+lastPoint.roni)}°C</text>
                    </g>
                {/if}
            </g>
        </svg>

        {#if hovered && hoverPoint}
            <div class="tooltip" class:flip={tooltipFlip} style="left:{pointer.x}px; top:{pointer.y}px;">
                <span class="tt-year">{fmtDate(hoverPoint.dt)}</span>
                <span class="tt-val">{fmt(+hoverPoint.roni)}°C</span>
            </div>
        {/if}
    {/if}
</div>

<style lang='scss'>
    @import '@/styles/variables';

    .graphic-wrapper {
        width: 100%;
        position: relative;
        height: 500px;

        @media screen and (max-width: 450px) {
            height: 400px;
        }
    }

    .locator {
        position: absolute;
        bottom: 38px;
        left: 30px;
        z-index: 3;
        pointer-events: none;

        @media screen and (max-width: 450px) {
            // top: 40px;
        }
    }

    .nino-band {
        fill: $red;
        opacity: 0.07;
        pointer-events: none;
    }

    .nino-band-label {
        fill: $red;
        font-size: 12px;
        font-weight: 600;
        text-anchor: middle;
        pointer-events: none;

        &.halo {
            fill: #fff;
            stroke: #fff;
            stroke-width: 3.5;
            stroke-linejoin: round;
        }
    }

    .year-line {
        stroke: $gray4;
        stroke-width: 1;
        pointer-events: none;

        &.nino {
            stroke: $red;
            stroke-width: 1.75;
        }

        &.current {
            stroke: $cnn-red;
            stroke-width: 2.5;
        }
    }

    // Fade the whole field while hovering so the active line stands out
    .lines.dim .year-line {
        opacity: 0.28;
        transition: opacity 0.12s ease;
    }

    .hover-line {
        fill: none;
        stroke: $gray9;
        stroke-width: 2;
        pointer-events: none;

        &.nino {
            stroke: $red;
            stroke-width: 2.5;
        }

        &.current {
            stroke: $cnn-red;
            stroke-width: 3;
        }
    }

    .hit-area {
        fill: none;
        stroke: transparent;
        stroke-width: 12;
        pointer-events: stroke;
        cursor: crosshair;
    }

    .hover-dot {
        fill: $gray9;
        stroke: #fff;
        stroke-width: 1.5;
        pointer-events: none;

        &.nino {
            fill: $red;
        }

        &.current {
            fill: $cnn-red;
        }
    }

    .year-label {
        font-size: 12px;
        font-weight: 700;
        fill: $red;
        pointer-events: none;
    }

    .year-leader {
        stroke: $red;
        stroke-width: 1;
        pointer-events: none;
    }

    .month-label {
        font-size: 11px;
        fill: #666;
        text-anchor: middle;
        pointer-events: none;

        &.start {
            text-anchor: start;
        }

        &.end {
            text-anchor: end;
        }
    }

    .current-marker {
        pointer-events: none;

        .pulse {
            fill: $cnn-red;
            transform-box: fill-box;
            transform-origin: center;
            animation: pulse 1.6s ease-out infinite;
        }

        .dot {
            fill: $cnn-red;
        }

        .marker-year {
            font-size: 12px;
            font-weight: 700;
            fill: $cnn-red;
        }

        .marker-value {
            font-size: 12px;
            fill: $gray9;
        }

        .halo {
            fill: #fff;
            stroke: #fff;
            stroke-width: 3.5;
            stroke-linejoin: round;
        }
    }

    @keyframes pulse {
        0% {
            transform: scale(1);
            opacity: 0.55;
        }
        100% {
            transform: scale(2.6);
            opacity: 0;
        }
    }

    .tooltip {
        position: absolute;
        pointer-events: none;
        transform: translate(12px, -50%);
        background: #fff;
        border: 1px solid $gray4;
        border-radius: 3px;
        padding: 4px 8px;
        font-size: 12px;
        line-height: 1.2;
        white-space: nowrap;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
        z-index: 4;

        &.flip {
            transform: translate(calc(-100% - 12px), -50%);
        }

        .tt-year {
            font-weight: 700;
            color: $gray11;
        }

        .tt-date {
            margin-left: 6px;
            color: $gray7;
        }

        .tt-val {
            margin-left: 6px;
            color: $gray9;
        }
    }
</style>
